import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ChessGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import chess from "../utils/chessEngine.mjs";
import {
  chessLeave,
  chessMove,
  chessPresence,
  chessSelect,
} from "../services/chessApi";
import {
  clearChessNotify,
  loadChessNotify,
  readLocalChessNotify,
  saveChessNotify,
} from "../services/chessNotify";
import ChessChat from "./ChessChat";

const MODES = [
  { id: "beginner", labelKey: "chessModeBeginner" },
  { id: "easy", labelKey: "chessModeEasy" },
  { id: "intermediate", labelKey: "chessModeIntermediate" },
  { id: "hard", labelKey: "chessModeHard" },
  { id: "master", labelKey: "chessModeMaster" },
];

const PIECE_SYMBOL = {
  k: "♚",
  q: "♛",
  r: "♜",
  b: "♝",
  n: "♞",
  p: "♟",
};

const PIECE_KEY = {
  k: "chessKing",
  q: "chessQueen",
  r: "chessRook",
  b: "chessBishop",
  n: "chessKnight",
  p: "chessPawn",
};

let pendingLeave = null;

const fill = (template, name) => template.replace("{name}", name);

const groupOpponents = (opponents) => {
  const groups = new Map();
  opponents.forEach((player) => {
    const first = (player.displayName || "").trim().charAt(0).toUpperCase();
    const key = /[A-Z]/.test(first) ? first : "#";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(player);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([letter, players]) => [
      letter,
      [...players].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" })
      ),
    ]);
};

function ChessMenu({ id, label, placeholder, valueLabel, children }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`chess-menu ${open ? "is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        id={id}
        className="chess-menu-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className={valueLabel ? "" : "is-placeholder"}>{valueLabel || placeholder}</span>
        <span className="chess-menu-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="chess-menu-panel" role="listbox" aria-label={label}>
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

const ChessGame = () => {
  const { t } = useLanguage();
  const { userId, displayName } = useFirebase();
  const [screen, setScreen] = useState("setup");
  const [mode, setMode] = useState(null);
  const [opponentKind, setOpponentKind] = useState("");
  const [opponents, setOpponents] = useState([]);
  const [opponentId, setOpponentId] = useState("");
  const [onlineGame, setOnlineGame] = useState(null);
  const [localState, setLocalState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [sending, setSending] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState("");
  const [notifyOn, setNotifyOn] = useState(false);
  const [playerAlert, setPlayerAlert] = useState(false);
  const thinkTimer = useRef(null);
  const notifyArmedRef = useRef(false);
  const savedNotifyRef = useRef(null);
  const notifyTouchedRef = useRef(false);
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const ignoredGameRef = useRef(null);
  const localRef = useRef(null);
  const opponentKindRef = useRef(opponentKind);
  const modeRef = useRef(mode);
  const userIdRef = useRef(userId);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    opponentKindRef.current = opponentKind;
  }, [opponentKind]);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    localRef.current = localState;
  }, [localState]);

  useEffect(() => {
    if (pendingLeave) {
      clearTimeout(pendingLeave);
      pendingLeave = null;
    }
    return () => {
      const id = userIdRef.current;
      pendingLeave = setTimeout(() => {
        pendingLeave = null;
        chessLeave(id);
      }, 700);
    };
  }, []);

  useEffect(() => () => clearTimeout(thinkTimer.current), []);

  useEffect(() => {
    if (opponentKind !== "human" || !userId) return undefined;
    let cancelled = false;
    const requestedMode = mode;

    const tick = async () => {
      try {
        const data = await chessPresence(userId, displayName, requestedMode);
        if (cancelled || modeRef.current !== requestedMode) return;
        setOpponents(data.opponents || []);
        setError((prev) => (prev ? "" : prev));
        if (data.game && data.game.id !== ignoredGameRef.current) {
          setOnlineGame((current) => {
            if (!current || current.id !== data.game.id) return data.game;
            const nextPly = data.game.ply || 0;
            const currPly = current.ply || 0;
            if (nextPly > currPly) return data.game;
            if (nextPly === currPly && data.game.status !== current.status) {
              return data.game;
            }
            return current;
          });
          setScreen("board");
        }
      } catch {
        if (!cancelled) setError(tRef.current("chessConnectError"));
      }
    };

    tick();
    const interval = setInterval(tick, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [opponentKind, userId, displayName, mode]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const local = readLocalChessNotify(userId);
    if (local && screenRef.current === "setup") {
      savedNotifyRef.current = local;
      setOpponentKind("human");
      setMode(local.mode);
      setNotifyOn(true);
      notifyArmedRef.current = true;
    }
    loadChessNotify(userId).then((saved) => {
      if (cancelled || notifyTouchedRef.current || screenRef.current !== "setup") return;
      if (saved?.on) {
        savedNotifyRef.current = saved;
        setOpponentKind("human");
        setMode(saved.mode);
        setNotifyOn(true);
        notifyArmedRef.current = true;
        return;
      }
      if (!readLocalChessNotify(userId)) {
        savedNotifyRef.current = null;
        notifyArmedRef.current = false;
        setNotifyOn(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!notifyOn || screen !== "setup" || opponentKind !== "human" || !mode) return;
    if (opponents.length === 0) {
      notifyArmedRef.current = true;
      return;
    }
    if (!notifyArmedRef.current) return;
    notifyArmedRef.current = false;
    savedNotifyRef.current = null;
    setPlayerAlert(true);
    setNotifyOn(false);
    clearChessNotify(userId);
  }, [notifyOn, screen, opponentKind, mode, opponents, userId]);

  useEffect(() => {
    if (!opponentId) return;
    if (!opponents.some((player) => player.userId === opponentId)) setOpponentId("");
  }, [opponents, opponentId]);

  useEffect(() => {
    if (screen !== "setup" || opponentKind !== "human") setPlayerAlert(false);
  }, [screen, opponentKind]);

  const isHuman = opponentKind === "human";

  const engineState = useMemo(() => {
    if (isHuman) {
      return onlineGame ? chess.fromSnapshot(onlineGame) : null;
    }
    return localState;
  }, [isHuman, onlineGame, localState]);

  const myColor = isHuman ? onlineGame?.you || "w" : "w";
  const legal = useMemo(
    () => (engineState ? chess.legalMoves(engineState) : []),
    [engineState]
  );

  const moveKey = engineState?.lastMove
    ? `${engineState.lastMove.from.r}${engineState.lastMove.from.c}${engineState.lastMove.to.r}${engineState.lastMove.to.c}-${engineState.turn}-${engineState.status}`
    : engineState?.status || "";

  useEffect(() => {
    setSelected(null);
    setPromotion(null);
  }, [moveKey]);

  const chooseKind = async (kind) => {
    if (kind === opponentKind) return;
    notifyTouchedRef.current = true;
    if (opponentKind === "human") {
      await chessLeave(userId);
      setOnlineGame(null);
    }
    setOpponentKind(kind);
    setOpponentId("");
    setOpponents([]);
    setError("");
  };

  const chooseMode = (nextMode) => {
    if (nextMode === mode) return;
    notifyTouchedRef.current = true;
    setMode(nextMode);
    setOpponentId("");
    setOpponents([]);
    setError("");
    if (notifyOn) {
      savedNotifyRef.current = { on: true, mode: nextMode };
      notifyArmedRef.current = true;
      saveChessNotify(userId, nextMode);
      return;
    }
    if (savedNotifyRef.current?.on && savedNotifyRef.current.mode === nextMode) {
      setNotifyOn(true);
      notifyArmedRef.current = true;
    }
  };

  const pressNotify = () => {
    notifyTouchedRef.current = true;
    if (notifyOn) {
      savedNotifyRef.current = null;
      notifyArmedRef.current = false;
      setNotifyOn(false);
      setPlayerAlert(false);
      clearChessNotify(userId);
      return;
    }
    if (!mode) return;
    if (opponents.length > 0) {
      savedNotifyRef.current = null;
      notifyArmedRef.current = false;
      setPlayerAlert(true);
      setNotifyOn(false);
      clearChessNotify(userId);
      return;
    }
    savedNotifyRef.current = { on: true, mode };
    notifyArmedRef.current = true;
    setNotifyOn(true);
    saveChessNotify(userId, mode);
  };

  const startComputer = async () => {
    if (opponentKindRef.current === "human") await chessLeave(userId);
    clearTimeout(thinkTimer.current);
    setThinking(false);
    setOnlineGame(null);
    setError("");
    setLocalState(chess.createInitialState());
    setScreen("board");
  };

  const handleSelect = async () => {
    if (selecting || !opponentKind) return;
    if (opponentKind === "computer") {
      if (!mode) return;
      await startComputer();
      return;
    }
    if (opponentKind !== "human" || !mode || !opponentId) return;
    setSelecting(true);
    setError("");
    try {
      const data = await chessSelect(userId, displayName, opponentId, mode);
      if (data.game) {
        ignoredGameRef.current = null;
        setOnlineGame(data.game);
        setScreen("board");
      }
    } catch (err) {
      if (err.payload?.opponents) setOpponents(err.payload.opponents);
      setError(err.payload?.error || t("chessAlreadyPlaying"));
    } finally {
      setSelecting(false);
    }
  };

  const playComputerMove = (state) => {
    clearTimeout(thinkTimer.current);
    if (!state || state.status !== "playing" || state.turn !== "b") {
      setThinking(false);
      return;
    }
    setThinking(true);
    thinkTimer.current = setTimeout(() => {
      const level = modeRef.current;
      if (!level || opponentKindRef.current === "human") {
        setThinking(false);
        return;
      }
      const current = localRef.current;
      const base = current && current.ply === state.ply ? current : state;
      if (!base || base.status !== "playing" || base.turn !== "b") {
        setThinking(false);
        return;
      }
      const move = chess.chooseAiMove(base, level);
      if (move) setLocalState(chess.applyMove(base, move));
      setThinking(false);
    }, 420);
  };

  const commitMove = async (move) => {
    setSelected(null);
    setPromotion(null);
    if (isHuman) {
      if (!onlineGame || sending) return;
      setSending(true);
      setError("");
      try {
        const data = await chessMove(userId, onlineGame.id, move);
        if (data.game) setOnlineGame(data.game);
      } catch (err) {
        if (err.payload?.game) setOnlineGame(err.payload.game);
        setError(err.payload?.error || t("chessConnectError"));
      } finally {
        setSending(false);
      }
      return;
    }

    const next = chess.applyMove(localState, move);
    if (!next) return;
    setLocalState(next);
    playComputerMove(next);
  };

  const onSquare = (r, c) => {
    if (!engineState || engineState.status !== "playing") return;
    if (engineState.turn !== myColor || thinking || sending) return;

    const piece = engineState.board[r][c];
    const fromHere = selected
      ? legal.filter(
          (move) =>
            move.from.r === selected.r &&
            move.from.c === selected.c &&
            move.to.r === r &&
            move.to.c === c
        )
      : [];

    if (fromHere.length > 1) {
      setPromotion(fromHere);
      return;
    }
    if (fromHere.length === 1) {
      commitMove(fromHere[0]);
      return;
    }
    if (piece && chess.colorOf(piece) === myColor) {
      setSelected({ r, c });
      return;
    }
    setSelected(null);
  };

  const playAgain = async () => {
    if (isHuman) {
      ignoredGameRef.current = onlineGame?.id || null;
      await chessLeave(userId);
      setOnlineGame(null);
      setSelected(null);
      setPromotion(null);
      setError("");
      setOpponentKind("");
      setMode(null);
      setOpponentId("");
      setNotifyOn(false);
      setPlayerAlert(false);
      notifyArmedRef.current = false;
      setScreen("setup");
      return;
    }
    clearTimeout(thinkTimer.current);
    setThinking(false);
    setLocalState(chess.createInitialState());
    setSelected(null);
    setPromotion(null);
  };

  const opponentName = (() => {
    if (!isHuman) return t("chessComputer");
    if (!onlineGame) return "";
    const other = myColor === "w" ? onlineGame.black : onlineGame.white;
    return other?.displayName || t("chessOpponent");
  })();

  const groups = groupOpponents(opponents);
  const chosenMode = MODES.find((entry) => entry.id === mode);
  const shownModeId = isHuman ? onlineGame?.mode || mode : mode;
  const shownMode = MODES.find((entry) => entry.id === shownModeId);
  const chosenPerson = opponents.find((player) => player.userId === opponentId);
  const canSelect =
    !selecting &&
    Boolean(opponentKind) &&
    Boolean(mode) &&
    (opponentKind === "computer" || Boolean(opponentId));
  const canMove =
    engineState &&
    engineState.status === "playing" &&
    engineState.turn === myColor &&
    !thinking &&
    !sending;

  const result = (() => {
    if (!engineState || engineState.status === "playing") return null;
    if (engineState.status === "stalemate" || engineState.status === "draw") {
      return {
        title: t("chessDraw"),
        detail:
          engineState.reason === "stalemate"
            ? t("chessStalemate")
            : t("chessInsufficient"),
      };
    }
    const won = engineState.winner === myColor;
    if (engineState.reason === "resign") {
      return {
        title: won ? t("chessYouWin") : t("chessYouLose"),
        detail: won ? t("chessOpponentLeft") : t("chessYouLeft"),
      };
    }
    return {
      title: won ? t("chessYouWin") : t("chessYouLose"),
      detail: t("chessCheckmate"),
    };
  })();

  const rowOrder = myColor === "b" ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7];
  const colOrder = myColor === "b" ? [7, 6, 5, 4, 3, 2, 1, 0] : [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <div className="chess-container">
      {playerAlert && screen === "setup" && (
        <div className="chess-player-alert" role="status">
          <button
            type="button"
            className="chess-player-alert-main"
            onClick={() => setPlayerAlert(false)}
          >
            {t("chessPlayerHere")}
          </button>
          <button
            type="button"
            className="chess-player-alert-close"
            aria-label={t("close")}
            onClick={() => setPlayerAlert(false)}
          >
            ×
          </button>
        </div>
      )}
      <div className={`chess-card ${screen === "board" ? "is-playing" : ""}`}>
        {screen === "setup" ? (
          <>
            <h1 className="chess-title">{displayName || t("chessYou")}</h1>
            <div className="chess-lobby">
              <label className="chess-label" htmlFor="chess-want">
                {t("chessIfYouWant")}
              </label>
              <ChessMenu
                id="chess-want"
                label={t("chessIfYouWant")}
                placeholder={t("chessWantPlaceholder")}
                valueLabel={
                  opponentKind === "human"
                    ? t("chessHuman")
                    : opponentKind === "computer"
                      ? t("chessComputer")
                      : ""
                }
              >
                {(close) =>
                  [
                    ["computer", t("chessComputer")],
                    ["human", t("chessHuman")],
                  ].map(([kind, name]) => (
                    <button
                      key={kind}
                      type="button"
                      role="option"
                      aria-selected={opponentKind === kind}
                      className={`chess-menu-item ${
                        opponentKind === kind ? "is-selected" : ""
                      }`}
                      onClick={() => {
                        chooseKind(kind);
                        close();
                      }}
                    >
                      {name}
                    </button>
                  ))
                }
              </ChessMenu>
            </div>

            <div className="chess-lobby">
              <label className="chess-label" htmlFor="chess-mode">
                {t("chessModeLabel")}
              </label>
              <ChessMenu
                id="chess-mode"
                label={t("chessModeLabel")}
                placeholder={t("chessModePlaceholder")}
                valueLabel={chosenMode ? t(chosenMode.labelKey) : ""}
              >
                {(close) =>
                  MODES.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      role="option"
                      aria-selected={mode === entry.id}
                      className={`chess-menu-item ${mode === entry.id ? "is-selected" : ""}`}
                      onClick={() => {
                        chooseMode(entry.id);
                        close();
                      }}
                    >
                      {t(entry.labelKey)}
                    </button>
                  ))
                }
              </ChessMenu>
            </div>

            {isHuman && (
              <div className="chess-lobby">
                <label className="chess-label" htmlFor="chess-player">
                  {t("chessPlayerLabel")}
                </label>
                <ChessMenu
                  id="chess-player"
                  label={t("chessPlayerLabel")}
                  placeholder={t("chessPlayerPlaceholder")}
                  valueLabel={chosenPerson?.displayName || ""}
                >
                  {(close) => {
                    if (!mode) {
                      return <p className="chess-menu-empty">{t("chessChooseModeFirst")}</p>;
                    }
                    if (groups.length === 0) {
                      return <p className="chess-menu-empty">{t("chessNoOpponents")}</p>;
                    }
                    return groups.map(([letter, players]) => (
                      <div key={letter} className="chess-menu-group">
                        <div className="chess-menu-letter">{letter}</div>
                        {players.map((player) => (
                          <button
                            key={player.userId}
                            type="button"
                            role="option"
                            aria-selected={opponentId === player.userId}
                            className={`chess-menu-item ${
                              opponentId === player.userId ? "is-selected" : ""
                            }`}
                            onClick={() => {
                              setOpponentId(player.userId);
                              close();
                            }}
                          >
                            {player.displayName}
                          </button>
                        ))}
                      </div>
                    ));
                  }}
                </ChessMenu>
                {opponents.length > 0 && (
                  <p className="chess-note">{t("chessYouAreListed")}</p>
                )}
              </div>
            )}

            <div className="chess-actions">
              <button
                type="button"
                className="chess-primary"
                disabled={!canSelect}
                onClick={handleSelect}
              >
                {selecting ? t("chessSelecting") : t("chessSelect")}
              </button>
            </div>
            {error && <p className="chess-error">{error}</p>}
            {isHuman && mode && opponents.length === 0 && (
              <div className="chess-notify-row">
                <p className="chess-note">{t("chessNoOpponents")}</p>
                <button
                  type="button"
                  className={`chess-notify ${notifyOn ? "is-on" : ""}`}
                  aria-pressed={notifyOn}
                  onClick={pressNotify}
                >
                  {t("chessNotifyMe")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="chess-play">
          <div className="chess-board-wrap">
            <div className="chess-players">
              <div>
                {opponentName}
                <span className="chess-player-sub">
                  {myColor === "w" ? t("chessBlack") : t("chessWhite")}
                </span>
              </div>
              <div>
                {displayName || t("chessYou")}
                <span className="chess-player-sub">
                  {myColor === "w" ? t("chessWhite") : t("chessBlack")}
                </span>
              </div>
            </div>
            {shownMode && <p className="chess-level">{t(shownMode.labelKey)}</p>}

            {result && (
              <div className="chess-result">
                <h2>{result.title}</h2>
                <p>{result.detail}</p>
              </div>
            )}

            <p className="chess-status">
              {result
                ? ""
                : !engineState
                  ? t("chessSelecting")
                  : thinking || engineState.turn !== myColor
                    ? fill(t("chessOpponentTurn"), opponentName)
                    : engineState.inCheck
                      ? `${t("chessCheck")} — ${t("chessYourTurn")}`
                      : t("chessYourTurn")}
            </p>
            {error && <p className="chess-error">{error}</p>}

            {promotion && (
              <div className="chess-promo" role="group" aria-label={t("chessPromotion")}>
                <span className="chess-label">{t("chessPromotion")}</span>
                {promotion.map((move) => (
                  <button
                    key={move.promotion}
                    type="button"
                    className="chess-promo-btn"
                    onClick={() => commitMove(move)}
                    aria-label={t(PIECE_KEY[move.promotion])}
                  >
                    {PIECE_SYMBOL[move.promotion]}
                  </button>
                ))}
              </div>
            )}

            <div className="chess-board" aria-label={t("chess")}>
              {rowOrder.map((r) =>
                colOrder.map((c) => {
                  const piece = engineState?.board?.[r]?.[c];
                  const isLight = (r + c) % 2 === 0;
                  const isSelected = selected && selected.r === r && selected.c === c;
                  const targetMoves = selected
                    ? legal.filter(
                        (move) =>
                          move.from.r === selected.r &&
                          move.from.c === selected.c &&
                          move.to.r === r &&
                          move.to.c === c
                      )
                    : [];
                  const isEnPassant =
                    engineState?.ep?.r === r && engineState?.ep?.c === c;
                  const isCapture =
                    targetMoves.length > 0 && (Boolean(piece) || isEnPassant);
                  const last = engineState?.lastMove;
                  const isLast =
                    last &&
                    ((last.from.r === r && last.from.c === c) ||
                      (last.to.r === r && last.to.c === c));
                  const king =
                    piece &&
                    piece.toLowerCase() === "k" &&
                    chess.colorOf(piece) === engineState.turn &&
                    (engineState.inCheck || engineState.status === "checkmate");
                  const label = piece
                    ? `${chess.colorOf(piece) === "w" ? t("chessWhite") : t("chessBlack")} ${t(
                        PIECE_KEY[piece.toLowerCase()]
                      )} ${chess.algebraic(r, c)}`
                    : chess.algebraic(r, c);

                  return (
                    <button
                      key={`${r}-${c}`}
                      type="button"
                      className={[
                        "chess-square",
                        isLight ? "light" : "dark",
                        isSelected ? "is-selected" : "",
                        isLast ? "is-last" : "",
                        king ? "is-check" : "",
                        targetMoves.length && isCapture ? "is-capture" : "",
                        targetMoves.length && !isCapture ? "is-target" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-label={label}
                      disabled={!canMove && !piece}
                      onClick={() => onSquare(r, c)}
                    >
                      <span
                        className={[
                          "chess-piece-slot",
                          piece
                            ? chess.colorOf(piece) === "w"
                              ? "chess-piece-white"
                              : "chess-piece-black"
                            : "is-empty",
                        ].join(" ")}
                        aria-hidden={piece ? undefined : true}
                      >
                        {PIECE_SYMBOL[piece ? piece.toLowerCase() : "p"]}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="chess-actions">
              {(opponentKind !== "human" || result) && (
                <button type="button" className="chess-primary" onClick={playAgain}>
                  {t("chessPlayAgain")}
                </button>
              )}
            </div>
          </div>
          {isHuman && (
            <ChessChat
              gameId={onlineGame?.id || `solo_${userId || "local"}`}
              userId={userId}
              displayName={displayName}
            />
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChessGame;
